import React, { useRef, useState } from "react";
import { patchGuardian, patchMember, patchUserData } from "../../services/httpClient";
import { Button, Spinner, Stack, Image } from "react-bootstrap";
import { useParams } from "react-router-dom";
import imageCompression from "browser-image-compression";

interface CaptureProfileState {
  userId: string | undefined;
  userProfileId: string | undefined;
  userName: string;
  userType: string;
  myCase?: boolean;
  profileImage: string;
  setProfileImage: (img: string) => void;
  setErrMsg: (msg: string) => void;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
}

const CaptureProfilePic = ({
  userId,
  userProfileId,
  userName,
  userType,
  myCase = true,
  profileImage,
  setProfileImage,
  setErrMsg,
  setShowToast,
  setToastText,
  setToastBody,
}: CaptureProfileState) => {
  const { id } = useParams();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const fileCaptureRef = useRef<HTMLInputElement | null>(null);
  const [showCamera, setShowCamera] = useState<boolean>(false);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const onClickSelect = (event: any) => {
    fileInputRef?.current?.click();
  };

  const handleFileChange = async (event: any) => {
    setErrMsg("");
    setIsImageLoading(true);

    const file = event.target.files[0];
    if (file) {
      if (file.type.startsWith("image/")) {
        // var reader = new FileReader();
        // console.log("File selected:", file);
        // reader.readAsDataURL(file);
        // reader.onload = function () {
        //   if (reader.result) {
        //     var base64Before = reader.result.toString().split(",")[1];
        // const base64BeforeSizeKB = Math.round((base64Before.length * 3) / 4 / 1024);
        // console.log("Base64 size before compression (KB):", base64BeforeSizeKB);
        //   }
        // };
        // reader.onerror = function (error) {
        //   setIsImageLoading(false);
        // };

        //compress image
        const compressedFile = await imageCompression(file, {
          maxSizeMB: 0.2,
          maxWidthOrHeight: 600,
          useWebWorker: true,
        });
        // console.log("Compressed file:", compressedFile);
        var compressedReader = new FileReader();
        compressedReader.readAsDataURL(compressedFile);
        compressedReader.onload = function () {
          if (compressedReader.result) {
            var base64After = compressedReader.result.toString().split(",")[1];
            // const base64AfterSizeKB = Math.round((base64After.length * 3) / 4 / 1024);
            // console.log("Base64 size after compression (KB):", base64AfterSizeKB);
            uploadFileData(base64After);
          }
        };
        compressedReader.onerror = function (error) {
          setIsImageLoading(false);
        };
      } else {
        setErrMsg("Please select a valid image file");
        setIsImageLoading(false);
      }
    } else {
      setIsImageLoading(false);
    }
  };

  async function uploadFileData(data: any) {
    var image;
    if (userProfileId) {
      image = {
        profile_id: userProfileId,
        profile_data: data,
      };
    } else {
      image = {
        profile_data: data,
      };
    }

    if (userType === "guardian") {
      patchGuardian(image, id, userId)
        .then((res: any) => {
          const base64String = "data:image/jpeg;base64, " + data;
          setProfileImage(base64String);

          setShowToast(true);
          const toast = userName;
          setToastText("Guardian Profile Image Updated");
          setToastBody(toast);
          setIsImageLoading(false);
        })
        .catch((err: any) => {
          setErrMsg(err?.response?.data?.msg);
          setIsImageLoading(false);
        });
    } else if (userType === "member") {
      patchMember(image, id, userId)
        .then((res: any) => {
          const base64String = "data:image/jpeg;base64, " + data;
          setProfileImage(base64String);

          setShowToast(true);
          const toast = userName;
          setToastText("Member Profile Image Updated");
          setToastBody(toast);
          setIsImageLoading(false);
        })
        .catch((err: any) => {
          setErrMsg(err?.response?.data?.msg);
          setIsImageLoading(false);
        });
    } else {
      patchUserData(image, id)
        .then((res: any) => {
          const base64String = "data:image/jpeg;base64, " + data;
          setProfileImage(base64String);

          setShowToast(true);
          const toast = userName;
          setToastText("User Profile Image Updated");
          setToastBody(toast);
          setIsImageLoading(false);
        })
        .catch((err: any) => {
          setErrMsg(err?.response?.data?.msg);
          setIsImageLoading(false);
        });
    }
  }

  const onClickStartCamera = async () => {
    try {
      setShowCamera(true);
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (err) {
      console.error("Camera error:", err);
      alert("Unable to access camera.");
    }
  };

  const onClickTakePicture = () => {
    if (!videoRef.current) return;
    setShowCamera(false);
    const canvas = document.createElement("canvas");
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(async (blob) => {
      if (blob) {
        const file = new File([blob], "photo.png", { type: "image/png" });
        var reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = function () {
          if (reader.result) {
            var base64Before = reader.result.toString().split(",")[1];
            const base64BeforeSizeKB = Math.round((base64Before.length * 3) / 4 / 1024);
            console.log("Base64 size before compression (KB):", base64BeforeSizeKB);
          }
        };
        reader.onerror = function (error) {
          setIsImageLoading(false);
        };

        //compress image
        const compressedFile = await imageCompression(file, {
          maxSizeMB: 0.2,
          maxWidthOrHeight: 600,
          useWebWorker: true,
        });
        var compressedReader = new FileReader();
        compressedReader.readAsDataURL(compressedFile);
        compressedReader.onload = function () {
          if (compressedReader.result) {
            var base64After = compressedReader.result.toString().split(",")[1];
            const base64AfterSizeKB = Math.round((base64After.length * 3) / 4 / 1024);
            console.log("Base64 size after compression (KB):", base64AfterSizeKB);
            uploadFileData(base64After);
          }
        };
        compressedReader.onerror = function (error) {
          setIsImageLoading(false);
        };
      }
    }, "image/png");
  };

  const onClickDeleteProfilePic = () => {
    setErrMsg("");
    const image = {
      profile_id: userProfileId,
      profile_data: null,
    };

    setIsImageLoading(true);

    if (userType === "guardian") {
      patchGuardian(image, id, userId)
        .then((res: any) => {
          setProfileImage("");
          setShowToast(true);
          const toast = userName;
          setToastText("Guardian Profile Image Removed");
          setToastBody(toast);
          setIsImageLoading(false);
        })
        .catch((err: any) => {
          setErrMsg(err?.response?.data?.msg);
          setIsImageLoading(false);
        });
    } else if (userType === "member") {
      patchMember(image, id, userId)
        .then((res: any) => {
          setProfileImage("");
          setShowToast(true);
          const toast = userName;
          setToastText("Member Profile Image Removed");
          setToastBody(toast);
          setIsImageLoading(false);
        })
        .catch((err: any) => {
          setErrMsg(err?.response?.data?.msg);
          setIsImageLoading(false);
        });
    } else {
      patchUserData(image, userId)
        .then((res: any) => {
          setProfileImage("");

          setShowToast(true);
          const toast = userName;
          setToastText("User Profile Image Removed");
          setToastBody(toast);
          setIsImageLoading(false);
        })
        .catch((err: any) => {
          setErrMsg(err?.response?.data?.msg);
          setIsImageLoading(false);
        });
    }
  };

  return (
    <>
      {isImageLoading ? (
        <Button size="sm" className="spinner-button spinner-button-main">
          <Spinner animation="border" style={{ color: "#F6893D" }} />
        </Button>
      ) : (
        <Stack gap={3}>
          {showCamera ? (
            <>
              <video ref={videoRef} autoPlay playsInline className="w-64 h-48 border" />
              <Stack direction="horizontal" gap={3} className="justify-content-center">
                <Button className="app-primary-color-inverse button w-100 ms-3" onClick={onClickTakePicture}>
                  Capture
                </Button>
                <Button className="app-primary-color button w-100 me-3" onClick={() => setShowCamera(false)}>
                  Cancel
                </Button>
              </Stack>
            </>
          ) : profileImage ? (
            <Image src={profileImage} rounded className="image-profile" />
          ) : (
            <Image src="/blank-profile-picture.jpg" className="image-profile" />
          )}
          {myCase && !showCamera && (
            <Stack direction="horizontal" gap={3} className="justify-content-center">
              <Button className="app-primary-color-inverse button w-100 ms-3" onClick={(e: any) => onClickSelect(e)}>
                Select Photo
              </Button>
              <Button className="app-primary-color-inverse button w-100 me-3" onClick={onClickStartCamera}>
                Take Photo
              </Button>
            </Stack>
          )}
          {myCase && profileImage && !showCamera && (
            <Button className="app-danger-color button mx-3" onClick={() => onClickDeleteProfilePic()}>
              Remove Image
            </Button>
          )}
        </Stack>
      )}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        accept="image/*"
        onChange={handleFileChange} // Handle the file change
      />
      <input
        type="file"
        ref={fileCaptureRef}
        style={{ display: "none" }}
        accept="image/*"
        capture="user"
        onChange={handleFileChange} // Handle the file change
      />
    </>
  );
};

export default CaptureProfilePic;
